import React from 'react';
import ReactDOM from 'react-dom/client';

import CookieConsentManagerContainer from 'analytics/CookieConsentManagerContainer';
import * as ReduxDOMRender from 'redux-stores/ReduxDOMRender';

function resetInvalidTrackingPreferencesCookie(): void {
  // The marketing website is misconfigured to record the Segment cookie with `domain=.benchling.com`,
  // which includes not only www.benchling.com, but also any <tenant>.benchling.com. The value of this
  // cookie for the application is different from the marketing since the Segment destinations are not
  // the same, which causes a frustrating behavior where the application continuously prompts the user
  // for consent since it only sees the incomplete marketing cookie. As a stop-gap until the marketing
  // website is fixed / to handle clients who still have the marketing cookie, we simply clear it out.
  // See ISD-1498 for more details.
  if (window.location.host === 'benchling.com') {
    // The benchling.com tenant by design writes to .benchling.com, so clearing it unconditionally
    // would always reprompt EU users, which is undesirable. Note that this means that an EU user who
    // uses both benchling.com and <tenant>.benchling.com will be reprompted every time they resume
    // using benchling.com.
    return;
  }
  document.cookie =
    'tracking-preferences=; domain=.benchling.com; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
}

/**
 * Mount a React component for the top-level consent management system, which will enable analytics
 * services if the user has consented (or if we determine that we don't need the user's consent).
 *
 * This must be called exactly once on page load for all pages.
 */
export default function mountCookieConsentManager(root: ReactDOM.Root | null): ReactDOM.Root {
  resetInvalidTrackingPreferencesCookie();
  const node = document.createElement('div');
  document.body.appendChild(node);
  return ReduxDOMRender.renderToDOM(<CookieConsentManagerContainer />, node, root);
}
